// This file is mostly clone of BatchCustomFunctionCalls.ts with some modifications to handle ExtraData API calls
// Originally these have been separated functions and this practice is continued here.
// More efficient way would be avoid code duplication and combine these two files and make the necessary changes to the functions.

import { ExtraData } from "./ApiCalls";
import { DRLog } from "./util/Logging";

// Batch custom function calls: https://learn.microsoft.com/en-us/office/dev/add-ins/excel/custom-functions-batching

const MAX_ERRORS = 5;
const BATCH_REQUEST_TIMEOUT_MS = 1000;
const API_RETRY = 1;
const API_RETRY_TIME_INTERVAL = 2000;
const API_RETRY_RESPONSE_CODE = 500;
const DRDATA_API_REQUEST_TIMEOUT = 15000;
const DRDATA_API_REQUEST_INTERVAL = 1000;

interface InvocationEntry {
  operation: string;
  args: any[];
  address: string;
  resolve: undefined | ((value: unknown) => void);
  reject: undefined | ((reason?: any) => void);
}

let _batch: InvocationEntry[] = [];
let _isBatchedRequestScheduled = false;

// This function encloses custom functions as individual entries,
// which have some additional properties so one can keep track of whether or not
// a request has been resolved or rejected.
export function _extraDataPushOperation(op: string, args: any[], address: string): Promise<string> {
  const invocationEntry: InvocationEntry = {
    operation: op,
    args: args,
    address: address,
    resolve: undefined,
    reject: undefined,
  };

  // Create a unique promise for this invocation,
  // and save its resolve and reject functions into the invocation entry.
  const promise = new Promise<string>((resolve, reject) => {
    invocationEntry.resolve = resolve;
    invocationEntry.reject = reject;
  });

  // Push the invocation entry into the next batch.
  _batch.push(invocationEntry);

  // If a remote request hasn't been scheduled yet,
  // schedule it after a certain timeout
  if (!_isBatchedRequestScheduled) {
    _isBatchedRequestScheduled = true;
    setTimeout(_makeRemoteRequest, BATCH_REQUEST_TIMEOUT_MS);
  }
  return promise;
}

// This function makes a request for remote processing of the whole batch,
// and matches the response batch to the request batch.
function _makeRemoteRequest() {
  // Copy the shared batch and allow the building of a new batch while you are waiting for a response.
  try {
    const batchCopy = _batch.splice(0, _batch.length);

    _isBatchedRequestScheduled = false;
    const DRDataCalls = batchCopy.filter((entry) => entry.operation === "DRExtraData");
    if (DRDataCalls.length > 0) {
      makeDRDataRequests(DRDataCalls);
    }
  } catch (error) {
    console.log(error);
  }
}

function makeDRDataRequests(dataCalls: InvocationEntry[]) {
  try {
    let apiCallGroups = {};
    for (const data of dataCalls) {
      DRLog("DEBUG", `${data.address}, DREXTRADATA(${data.args})`);
    }
    dataCalls.forEach((call) => {
      // First we remove account numbers and period numbers from the call as we want to batch calls that
      // are otherwise the same but differ in these arguments.
      const argsWithoutAccNoOrPeriod = call.args.filter((_value, index) => index !== 2 && index !== 4);

      // We use the remaining arguments as an identifier key to make parsing through all the different
      // calls as quick as possible
      const identifierKey = argsWithoutAccNoOrPeriod.reduce((ac, curVal) => ac.toString() + curVal, "");

      // This if/else tree structure goes through all the batched calls and determines how many calls is
      // required
      if (apiCallGroups[identifierKey]) {
        // First we check if there is need for a new DRData Call
        const accountNo = call.args[2];

        if (apiCallGroups[identifierKey][accountNo]) {
          // Second we check if a call for a certain account name already exists
          apiCallGroups[identifierKey][accountNo].push(call);
        } else {
          // Create new account
          const newAcc = { [accountNo]: [call] };
          apiCallGroups[identifierKey] = { ...apiCallGroups[identifierKey], ...newAcc };
        }
      } else {
        // Create a new call (We don't want to get here if only the account or period has changed)
        const accountNo = call.args[2];
        const newGroup = { [identifierKey]: { [accountNo]: [call] } };
        apiCallGroups = { ...apiCallGroups, ...newGroup };
      }
    });

    const apiCallGroupsArr = Object.values(apiCallGroups);
    var promise = Promise.resolve();
    apiCallGroupsArr.forEach((callGroup: Object, index) => {
      let extraFieldArray: string[] = [];
      // Wrap the iteration of CallGroups as promise to set a delay of 100ms due to API validation issues
      promise = promise.then(function () {
        const allAccounts = Object.values(callGroup);
        allAccounts.forEach((accountCalls: InvocationEntry[]) => {
          accountCalls.forEach((element) => {
            const extraId = element.args[4] as string;
            if (!extraFieldArray.includes(extraId)) {
              extraFieldArray.push(extraId);
            }
          });
        });
        const exampleEntry: InvocationEntry = Object.values(callGroup)[0][0];
        let argsWithoutAccNoOrExtraField = exampleEntry.args.filter((_value, index) => index !== 2 && index !== 4);

        /* The code below was added to handle empty parameters as requested by Vesa for legacy support */
        const reverseArgsWithoutAccNoOrExtraField = argsWithoutAccNoOrExtraField.reverse();
        reverseArgsWithoutAccNoOrExtraField.forEach((value, index) => {
          if (index !== 0 && value === undefined && reverseArgsWithoutAccNoOrExtraField[index - 1] !== undefined) {
            reverseArgsWithoutAccNoOrExtraField[index] = "";
          }
        });
        argsWithoutAccNoOrExtraField = reverseArgsWithoutAccNoOrExtraField.reverse();

        const accountNos: string[] = Object.keys(callGroup);
        const apiCallArgumentString = argsWithoutAccNoOrExtraField.reduce((ac, curVal, curIndex) => {
          // We need to check that curVal is undefined instead of curVal!, as it might be false.
          if (curVal !== undefined && curIndex !== 0) {
            return ac + "§" + curVal;
          } else if (curIndex === 0) {
            return ac + curVal;
          } else {
            return ac;
          }
        }, "");
        const response = ExtraData(
          apiCallArgumentString,
          accountNos,
          extraFieldArray,
          DRDATA_API_REQUEST_TIMEOUT * (index + 1)
        );

        // TODO: Possibly move this following resolving section fully to the ClausionApi file
        //       to make error handling clearer (may add unwanted complexity so move on with caution)

        response
          .then((res) => {
            allAccounts.forEach((account: InvocationEntry[]) =>
              account.forEach(async (call) => {
                if (!res) {
                  // There was an error and undefined was returned
                  call.resolve(0);
                  return;
                }
                if (!res["$values"]) {
                  // No error happened but values were not found with given parameters
                  call.resolve(res);
                  return;
                }
                const relevantValues = res["$values"].filter((val) => {
                  return val.AccountUdid.toUpperCase() === call.args[2].toUpperCase();
                })[0];
                if (relevantValues) {
                  const dataOfPeriod = relevantValues.AccountValues[call.args[4].toString()];
                  // TODO: The below was conditionally wrapped which resulted in value error if the returned value is 0.
                  //       Confirm if this is supposed to be conditionally resolved (if dataOfPeriod).
                  if (isNaN(dataOfPeriod)) {
                    call.resolve(dataOfPeriod);
                  } else {
                    call.resolve(Number(dataOfPeriod));
                  }
                }

                call.resolve(0); // Temporary outcome untill back-end changes are made
                return;
              })
            );
          })
          .catch((error) => {
            console.error(error);
            // Reject all affected calls so Excel does not stay busy
            allAccounts.forEach((account: InvocationEntry[]) =>
              account.forEach((call) => {
                if (call.reject) {
                  call.reject(error);
                } else if (call.resolve) {
                  call.resolve(0); // fallback if reject is not set
                }
              })
            );
          });

        return new Promise(function (resolve) {
          setTimeout(resolve, DRDATA_API_REQUEST_INTERVAL);
        });
      });
    });
  } catch (error) {
    console.log(error);
  }
}
